import ChartMetric from '@/Enums/Chart/Metric.enum';
import InsightCallout from '@/components/InsightCallout';
import { selectFootTrafficTradeAreaInsight } from '@/helpers/attribution/footTrafficInsights';
import { downloadCSV } from '@/helpers/downloadCSV';
import { useAppSelector } from '@/store/hooks';
import { selectFootTrafficTradeAreaReport } from '@/store/selectors/attribution/footTraffic.selectors';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { type ChartData } from 'chart.js';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import VerticalChart from '../Charts/VerticalChart';
import GraphCard from './BaseCard/GraphCard';

const label = 'Trade Area';
const PRIMARY = '#193B2C';

type Side = 'home' | 'work';

const TradeAreaCard = () => {
  const { loading, tradeAreaReport } = useAppSelector(selectFootTrafficTradeAreaReport);
  const [side, setSide] = useState<Side>('home');
  const { text: insightText } = useAppSelector(selectFootTrafficTradeAreaInsight(side));

  const bands = tradeAreaReport?.[side];
  const segments = bands?.segments ?? {};

  const chartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: Object.keys(segments),
      datasets: [
        {
          label: ChartMetric.FootTrafficVisits,
          data: Object.values(segments).map(v => Math.round(v * 1000) / 10),
          backgroundColor: [PRIMARY],
          borderColor: 'white',
        },
      ],
    }),
    [segments],
  );

  const noData = useMemo<boolean>(() => Object.values(segments).every(v => !v), [segments]);

  const onDownload = useCallback(() => {
    if (loading) return;
    const csvRows = Object.entries(segments).map(([band, pct]) => ({
      Band: band,
      'Share %': (pct * 100).toFixed(2),
    }));
    downloadCSV(csvRows, `${label} (${side})`);
  }, [segments, loading, side]);

  const handleSide = (_: MouseEvent<HTMLElement>, value: Side | null) => {
    if (value) setSide(value);
  };

  const primaryRadiusLabel =
    bands?.primaryRadiusMiles != null
      ? `${bands.primaryRadiusMiles >= 1 ? bands.primaryRadiusMiles : bands.primaryRadiusMiles.toFixed(1)} mi`
      : '—';
  const primaryRadiusPct =
    bands?.primaryRadiusPercent != null ? `${Math.round(bands.primaryRadiusPercent * 100)}%` : '—';

  return (
    <GraphCard label={label} loading={loading} noData={noData} onDownload={onDownload} allowPrint>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: '30px' }}>
          <Box>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Primary Radius</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{primaryRadiusLabel}</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Within Primary Radius</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{primaryRadiusPct}</Typography>
          </Box>
        </Box>
        <ToggleButtonGroup size='small' exclusive value={side} onChange={handleSide} aria-label='trade area origin'>
          <ToggleButton value='home' aria-label='home origin' sx={{ textTransform: 'none' }}>
            Home
          </ToggleButton>
          <ToggleButton value='work' aria-label='work origin' sx={{ textTransform: 'none' }}>
            Work
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <VerticalChart chartData={chartData} legendLabel={ChartMetric.FootTrafficVisits} />
      <InsightCallout text={insightText} loading={loading} />
    </GraphCard>
  );
};

export default TradeAreaCard;
